import React, { useEffect, useMemo, useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import {
  StyledEngineProvider,
} from "@mui/material";

import { SingleShow } from "../../../models/Show";
import {
  Modal,
  NavigationBar,
  Stepper,
  TicketDetails,
  TicketSummaryPanel,
} from "../../../components";
import { BookingInterestInterface } from "../../../models/BookingInterest";
import { createBookingInterest } from "../../../services/BookingInterestService";
import TermsStep from "./Steps/Terms/TermsStep";
import TicketsStep from "./Steps/Tickets/TicketsStep";
import Success from "./Success/Success";
import SessionStep from "./Steps/Session/SessionStep";
import BookingRegistrationFlow from "./Steps/Registration/BookingRegistrationFlow";
import { readSession } from "../../../services/SessionService";
import { FormPage } from "./BookingManager.styles";
import { SessionInterface } from "src/models/Session";
import { IBookingTicket } from "../../../models/BookingTicket";
import { BookingInterestStatus } from "../../../../enums/BookingInterestStatus";
import SuccessInvitedToTicket from "./SuccessInvitedToTicket/SuccessInvitedToTicket";
import { useAuthContext } from "src/contexts/AuthContext";
import { SingleSession } from "src/models/Session";
import SuccessInvitedToPriorityTicket from "./SuccessInvitedToPriorityTicket copy/SuccessInvitedToPriorityTicket";
import SuccessPriority from "./SuccessPriority/SuccessPriority";
import { useQuery } from "@tanstack/react-query";
import { minutesToMilliseconds } from "src/utils/dates";
import { arePriorityAllocationSessionsFull } from "src/services/AllocationsService";
import { usePriorityAllocationStore } from "src/store/usePriorityAllocationStore";
import { Modals, useModal } from "src/contexts/ModalContext";
import { isSessionInLastMinPhase } from "src/utils/sessionUtils";

interface BookingManagerProps extends WithRouterProps {
  show: SingleShow;
  promoCode: string | undefined;
  priority: string | undefined;
}

const BookingManager: React.FC<BookingManagerProps> = ({
  show,
  router,
  promoCode,
  priority
}) => {
  const { user } = useAuthContext();

  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState<SingleSession>();
  const [step, setStep] = useState(0);
  const [bookingInterestId, setBookingInterestId] = useState<
    number | undefined | null
  >(null);
  const [newBookingInterestStatus, setNewBookingInterestStatus] = useState<
    keyof typeof BookingInterestStatus | undefined | null
  >();
  const [bookingInterestError, setBookingInterestError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [continueWithNonPriority, setContinueWithNonPriority] = useState<boolean>(false);
  const [resolvedIsPriority, setResolvedIsPriority] = useState<boolean>(false);

  const [registrationMode, setRegistrationMode] = useState<boolean>(false);
  const [registrationEmail, setRegistrationEmail] = useState<string>("");
  const [registrationFirstName, setRegistrationFirstName] = useState<string>("");
  const [pendingSession, setPendingSession] = useState<SessionInterface | null>(null);

  const [ticketSummaryPanelContent, setTicketSummaryPanelContent] = useState<
    TicketDetails[]
  >([]);

  const setPriorityAllocFullSessions = usePriorityAllocationStore((state) => state.setPriorityAllocFullSessions);
  const updatePriorityAllocFullSession = usePriorityAllocationStore((state) => state.updatePriorityAllocFullSession);

  const { openModal, closeModal, modalState } = useModal();

  const isSessionPriorityActiveAndNotFull = usePriorityAllocationStore(
    (state) => state.isSessionPriorityActiveAndNotFull
  );

  useEffect(() => {
    sessionStorage.removeItem("formValues");
    sessionStorage.removeItem("ticketNumber");
  }, []);

  const {
    data: priorityAllocQueryData,
    isSuccess: isSuccesspriorityAllocQuery,
  } = useQuery({
    queryKey: ["arePriorityAllocationsFull", priority, session?.id],
    queryFn: async () => await arePriorityAllocationSessionsFull(priority!, session?.id),
    enabled: Boolean(priority),
    staleTime: minutesToMilliseconds(0.5),
    refetchInterval: minutesToMilliseconds(0.5)
  });

  useEffect(() => {
    if (priorityAllocQueryData?.data) {
      if (session?.id) {
        updatePriorityAllocFullSession(session.id, priorityAllocQueryData.data[session.id])
        if (priorityAllocQueryData.data[session?.id]?.isFull && modalState === Modals.None && !continueWithNonPriority && !resolvedIsPriority) {
          openModal(Modals.PriorityTicketsFullyBooked, (viewPrioritySessions?: boolean) => {
            if (viewPrioritySessions) {
              window.location.reload();
            } else {
              closeModal();
              setContinueWithNonPriority(true);
            }
          })
        }
      } else {
        setPriorityAllocFullSessions(priorityAllocQueryData.data);
      }
    }
  }, [isSuccesspriorityAllocQuery, setPriorityAllocFullSessions, priorityAllocQueryData]);

  const isPriorityActive = useMemo(() => {
    if (!session) return false;
    return isSessionPriorityActiveAndNotFull(session.id!);
  }, [session, isSessionPriorityActiveAndNotFull]);

  const selectSession = (sessionId: number, continueWithNonPriority?: boolean) => {
    if (continueWithNonPriority) {
      setContinueWithNonPriority(true)
    }

    readSession(Number(sessionId), promoCode).then((response) => {
      const newSession = response.data;

      setSession(newSession);
      setStepAndResetScroll(step + 1);
    });
  };

  const submitBookingInterest = (bookingInterest: BookingInterestInterface, magicLink: string | undefined) => {
    setSubmitting(true);
    createBookingInterest(
      bookingInterest,
      magicLink
    ).then((response) => {
      setBookingInterestId(response.data?.bookingInterestId);
      setNewBookingInterestStatus(response.data?.bookingInterestStatus);
      setSubmitting(false);
      if (response.error) {
        setBookingInterestError(true);
        if (typeof response.error === "string") {
          setErrorMessage(response.error);
        } else {
          setErrorMessage(response.error.title);
        }
      } else {
        sessionStorage.removeItem("formValues");
        sessionStorage.removeItem("ticketNumber");
        setStepAndResetScroll(step + 1);
      }
    });
  };

  const submit = (relatedBookingInterestFormValues: IBookingTicket[]) => {
    setSubmitting(true);
    (async () => {
      const bookingInterest: BookingInterestInterface = {
        sessionId: session?.id || 0,
        relatedBookingInterests: relatedBookingInterestFormValues?.map(
          (ticket) => {
            ticket.accessibility = ticket.accessibility === "None" ? null : ticket.accessibility
            return { user: ticket };
          }
        ),
      };
      try {
        let magicLink: string | undefined;
        if (resolvedIsPriority) {
          magicLink = priority;

          const fullCheck = await arePriorityAllocationSessionsFull(priority!, session?.id);
          if (fullCheck?.data && session?.id && fullCheck.data[session.id]?.isFull) {
            setSubmitting(false);
            openModal(Modals.PriorityTicketsFullyBooked, (viewPrioritySessions?: boolean) => {
              if (viewPrioritySessions) {
                window.location.reload();
              } else {
                closeModal();
                setContinueWithNonPriority(true);
                setResolvedIsPriority(false);
                submitBookingInterest(bookingInterest, undefined);
              }
            });
            return;
          }
        }
        submitBookingInterest(bookingInterest, magicLink);
      } catch (error) {
        setErrorMessage("An error occurred while saving the ticket requests!");
        setBookingInterestError(true);
      }
    })();
  };

  const getInitialTicketHolders = (isPriorityAllocActive?: boolean): TicketDetails[] => {
    let ticketHolders: TicketDetails[] = [
      {
        user: {
          firstName: user?.firstName!,
          lastName: user?.lastName!,
          email: user?.email!,
        },
        assignedFirstName: user?.firstName!,
        assignedLastName: user?.lastName!,
        isLeadBooker: true,
        accessibility: user?.accessibility ?? '',
        otherAccessibility: user?.otherAccessibility ?? '',
        hasAccessibilityRequirements: (!!user?.accessibility?.trim() && user?.accessibility != "None") || !!user?.otherAccessibility?.trim(),
        id: 0,
        isPriorityTicket: isPriorityAllocActive,
        ticketIconStyle: isPriorityAllocActive ? "gold-outlined" : "dotted",
        accessibilityRequestStatus: undefined,
        userAgeAtBooking: user?.age,
      },
    ];
    return ticketHolders;
  }

  useEffect(() => {
    setTicketSummaryPanelContent(getInitialTicketHolders(isPriorityActive));
  }, [user, isPriorityActive])

  const setTicketHolders = (
    relatedBookingInterestFormValues: IBookingTicket[],
    isPriorityAllocActive?: boolean
  ) => {
    if (ticketSummaryPanelContent) {
      const ticketHolders = ticketSummaryPanelContent.map((holder) => ({
        ...holder,
        isPriorityTicket: isPriorityAllocActive,
        ticketIconStyle: (isPriorityAllocActive ? "gold-outlined" : "dotted") as "gold-outlined" | "dotted",
      }));

      relatedBookingInterestFormValues.map(
        (relatedBookingInterest: IBookingTicket) => {
          ticketHolders.push({
            user: {
              firstName: relatedBookingInterest.firstName,
              lastName: relatedBookingInterest.lastName,
            },
            assignedFirstName: relatedBookingInterest.firstName,
            assignedLastName: relatedBookingInterest.lastName,
            userAgeAtBooking: relatedBookingInterest.age,
            accessibility: user?.accessibility ?? '',
            otherAccessibility: user?.otherAccessibility ?? '',
            hasAccessibilityRequirements: (!!relatedBookingInterest.accessibility?.trim() && relatedBookingInterest.accessibility != "None") || !!relatedBookingInterest.otherAccessibility?.trim(),
            id: relatedBookingInterest.bookingInterestId!,
            isPriorityTicket: isPriorityAllocActive,
            ticketIconStyle: isPriorityAllocActive ? "gold-outlined" : "dotted",
            accessibilityRequestStatus: undefined,
          });
        }
      );

      setTicketSummaryPanelContent(ticketHolders);
    }
  };

  const setStepAndResetScroll = (value: number) => {
    window.scrollTo({
      top: 0,
      left: 0,
    });
    setStep(value);
  }

  const nextStep = (isPriority?: boolean) => {
    const formValues = sessionStorage.getItem("formValues");
    const relatedBookingInterestFormValues = formValues
      ? JSON.parse(formValues)
      : null;

    if (step === 1) {
      setResolvedIsPriority(!!isPriority);
      setTicketHolders(relatedBookingInterestFormValues, isPriority);
    }
    if (step === 2) {
      submit(relatedBookingInterestFormValues);
    } else {
      setStepAndResetScroll(step + 1)
    }
  };

  const prevStep = () => {
    if (step == 2 || step == 1) {
      setTicketSummaryPanelContent(getInitialTicketHolders(isPriorityActive));
    }
    setStepAndResetScroll(step - 1);

    setBookingInterestError(false);
    setErrorMessage("");
  };

  const handleRegisterSuccess = (email: string, firstName: string, selectedSession: SessionInterface) => {
    setRegistrationEmail(email);
    setRegistrationFirstName(firstName);
    setPendingSession(selectedSession);
    setRegistrationMode(true);
  };

  const handleRegistrationComplete = () => {
    setRegistrationMode(false);
    if (pendingSession?.id) {
      selectSession(pendingSession.id);
    }
  };

  const minAge = session?.overrideMinimumAgeRequirement || show?.defaultMinimumAgeRequirement;

  if (registrationMode && pendingSession) {
    return (
      <StyledEngineProvider injectFirst>
        <BookingRegistrationFlow
          email={registrationEmail}
          firstName={registrationFirstName}
          selectedSession={pendingSession}
          showName={show?.name}
          showMinimumAge={show?.defaultMinimumAgeRequirement}
          onRegistrationComplete={handleRegistrationComplete}
          onBackToSessionSelection={() => setRegistrationMode(false)}
        />
      </StyledEngineProvider>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <NavigationBar showHomeIconOnMobile={step !== 0 && step !== 3} showMenu={step === 0}>
        {step !== 0 && step !== 3 && <Stepper stepLabels={['Session', 'Tickets', 'Terms']} activeStepIndex={step} />}
      </NavigationBar>
      {step === 0 && (
        <SessionStep
          user={user}
          promoCode={promoCode}
          show={show}
          onNextButtonClick={selectSession}
          onRegisterSuccess={handleRegisterSuccess}
        />
      )}
      <FormPage>
        <>
          {(step === 1 || step === 2) && (
            <>
              {step === 1 && (
                <TicketsStep
                  showId={show?.id}
                  onNextButtonClick={nextStep}
                  onPrevButtonClick={prevStep}
                  user={user}
                  minAge={Number(minAge)}
                  isPriorityActive={isPriorityActive}
                  sessionId={session?.id}
                  isLastMinPhase={isSessionInLastMinPhase(session)}
                />
              )}
              {step === 2 && (
                <TermsStep
                  submitting={submitting}
                  show={show as SingleShow}
                  session={session}
                  onNextButtonClick={nextStep}
                  onPrevButtonClick={prevStep}
                  errorMessage={errorMessage}
                  bookingInterestError={bookingInterestError}
                />
              )}
              <TicketSummaryPanel
                showName={show?.name}
                session={session}
                ticketHolders={ticketSummaryPanelContent}
                showMinimumAge={minAge}
              />
            </>
          )}

          {/* Consolidated flow (TTT-688): applying can now end in four different places, and
              the closing screen has to say which one. Applying inside the last-min phase
              joins the last min queue, and if a seat is free the offer goes out in the same
              request — as an SMS/check-in offer (AttendanceSMSReminderSent), not an
              InvitedToTicket. Telling that person they are "on the list" and that offers
              arrive 1 to 2 weeks before the show is simply untrue. */}
          {step === 3 &&
            ((newBookingInterestStatus === "InvitedToTicket" ||
              newBookingInterestStatus === "AttendanceSMSReminderSent") ? (
              resolvedIsPriority ? (
                <SuccessInvitedToPriorityTicket
                  firstName={user?.firstName}
                  bookingInterestId={bookingInterestId}
                  ticketHolders={ticketSummaryPanelContent}
                  session={session}
                  showName={show?.name}
                />
              ) : (
                <SuccessInvitedToTicket
                  firstName={user?.firstName}
                  bookingInterestId={bookingInterestId}
                  variant={
                    newBookingInterestStatus === "AttendanceSMSReminderSent"
                      ? "lastMinOffer"
                      : "emailOffer"
                  }
                />
              )
            ) : resolvedIsPriority ? (
              <SuccessPriority
                firstName={user?.firstName}
                bookingInterestId={bookingInterestId}
                accessibilityPending={newBookingInterestStatus === "InterestRegistered"}
              />
            ) : (
              <Success
                firstName={user?.firstName}
                bookingInterestId={bookingInterestId}
                variant={newBookingInterestStatus === "JoinedLastMinQueue" ? "lastMin" : "standard"}
              />
            ))}
        </>
      </FormPage>
    </StyledEngineProvider>
  );
};

export default withRouter(BookingManager);
